import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import Button from 'src/components/button';
import Slider from 'src/components/slider';
import { EditBannerComponent } from '../shareSettings/editBanner';
import Toast from 'src/components/toast';
import Tooltip from 'src/components/tooltip';
import WarningMessage from 'src/components/warningMessage';

import { invalidateLinkedInPost, invalidateLinkedInPosts, shareLinkedInPostRequest } from 'src/redux/actions/integrations/linkedIn';
import { FETCH, LINKEDIN } from 'src/query/queryKeys';

import { ContentType } from 'src/types/common';

import { FileService } from 'src/utils/file.service';

import _ from 'lodash';
import cx from 'classnames';
import styles from './styles.module.scss'
import { Media } from 'src/types/media';
import { Dropdown } from 'react-bootstrap';
import { invalidateLinkedInPostTones, repharseLinkedInPostDescription } from 'src/redux/actions/shareTemplates';
import { IRephraseTones } from 'src/types/shareTemplate';
import { useAssetAdoption } from 'src/redux/actions/adoption/asset';
import { Medium } from 'src/constants/adoption/sourceTypes';
import DescriptionEditor from './editor';
import CollectionWidgetManager from 'src/utils/collections.widget.service';
import { SchedulePicker } from './schedulePicker';
import { ScheduledPosts } from './scheduledPosts';
import { parseHTMLtoString } from 'src/utils/helper';
import { useLinkedInPostCountQuery } from 'src/query/integrations/linkedin';

const DefaultUserAvatar = '/images/default_user_profile_background.png'
const LinkedIn = '/images/linkedIn.svg'
const Seek = '/images/seek.gif';
const EmptyMessage = '/images/kiteErrorMessage.svg'
interface ILinkedInPostProps {
  entityType: ContentType,
  onCancel: () => void,
  entity: any,
  show: boolean,
  isLinkedInIntegrationActive: boolean,
  isLinkedInIntegrationExpired: boolean,
  shareUrl: IShareUrl,
}

interface IDescriptionTune {
  title: DescriptionToneEnum,
  description: string,
  active?: boolean
}

enum DescriptionToneEnum {
  FORMAL = "Formal",
  INFORMAL = "Informal",
  LIGHTHEARTED = "Light Hearted"
}

export interface IShareUrl {
  id: string;
  url: string;
}

const LinkedInPost: React.FC<ILinkedInPostProps> = ({ entityType, shareUrl, onCancel, entity, show, isLinkedInIntegrationExpired, isLinkedInIntegrationActive }) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const user = useSelector((state) => _.get(state, 'users.me', {}));

  const isPosting = useSelector((state) => _.get(state, "integrations.linkedIn.post.posting", false));
  const posted = useSelector((state) => _.get(state, "integrations.linkedIn.post.succeeded", false));
  const errorInPosting = useSelector((state) => _.get(state, "integrations.linkedIn.post.error", null));

  const [description, setDescription] = useState<string>('');
  const [banner, setBanner] = useState<Media>();
  const [descriptionsForTones, setDescriptionsForTones] = useState<IDescriptionTune[]>(initialDescriptionTones);
  const activeTone = _.find(descriptionsForTones, { active: true });

  const [showEditBanner, setShowEditBanner] = React.useState<boolean>(false);
  const [emptyDescriptionError, setEmptyDescriptionError] = React.useState<boolean>(false);
  const [disableRephrase,  setDisableRephrase] = React.useState<boolean>(false);
  const [scheduleLater, setScheduleLater] = React.useState<boolean>(false);
  const [showAllPosts, setShowAllPosts] = React.useState<boolean>(false);
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [scheduleTimeZone, setScheduleTimeZone] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const getFormattedTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil((minutes + 1) / 15) * 15;
    now.setMinutes(roundedMinutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    return now;
  };

  const [selectedTime, setSelectedTime] = useState<Date>(getFormattedTime());

  const shareTemplates = useSelector((state: any) => state.shareTemplates);
  const shareTemplate = shareTemplates[entity.id];
  const isRephrasing = shareTemplate?.rephrase?.loading;

  const shareTemplateData = shareTemplate?.item;

  const widgetManager = React.useRef(new CollectionWidgetManager());
  
  const [sliderWidth, setSliderWidth] = useState<string>("");
  const [isWordLimitExceeded, setIsWordLimitExceeded] = useState<boolean>(false);
  const {trackAssetShared, trackScheduledPostsClicked} = useAssetAdoption();

  const { data: linkedInData } = useLinkedInPostCountQuery(entity.id);
  const { postCount } = linkedInData || { postCount: { posted: 0, scheduled: 0 } };

  useEffect(() => {
    if (!isPosting && posted) {
      onCancel();
      toast(<Toast.Sucess message={`Successfully ${scheduleLater ? "Scheduled" : "Posted"}`} />, {
        autoClose: 3000
      });
      if (entityType === ContentType.Asset) {

        if(scheduleLater)
          trackScheduledPostsClicked();
        } else {
          const fileType = FileService.getFileType(entity.metadata);
          
          trackAssetShared({
          assetType: fileType,
          shareMedium: Medium.LINKEDIN
        });
      }

      invalidateData();
      if (entityType === ContentType.Asset && entity?.id) {
        queryClient.invalidateQueries({ queryKey: [LINKEDIN, FETCH, entity.id] });
      }
      handleClose();
    }
    else if (!isPosting && !posted && errorInPosting) {
      var errorMessage = "Failed to Post";
      const errorCode = _.get(errorInPosting, "errors[0].code", null);
      const errorCodesWithMessage = [50006, 50007]

      if (errorCode && errorCodesWithMessage.includes(errorCode)) {
        errorMessage = _.get(errorInPosting, "errors[0].message", "Failed to Post");
      }

      toast(<Toast.Failure message={errorMessage} />, {
        autoClose: 3000
      });
      onCancel();
      invalidateData();
    }
  }, [posted, isPosting, errorInPosting])

  useEffect(() => {

    if (!show) {
      invalidateData();
    }

  }, [show])

  useEffect(() => {
    setBanner(shareTemplateData?.mediaDTO || _.get(entity, "icon", null))
    setDescription(shareTemplateData?.message || '')
  }, [shareTemplateData])

  useEffect(() => {

    const tones = _.get(shareTemplate, "tones", {});
    if (show) {
      setDescriptionsForTones(descriptionsForTones.map((item) => {
        const trimmedTone = _.replace(item.title, " ", "");
        const tone = _.toUpper(trimmedTone) as IRephraseTones;
        if (tones[tone]) {
          if (item.active) {
            setDescription(tones[tone])
          }
          return { ...item, description: tones[tone] }
        }
        return item;
      }))
    }

  }, [shareTemplate?.tones])


  useEffect(() => {
    return () => {
      invalidateData();
    }
  }, [])

  const invalidateData = () => {
    setBanner(shareTemplateData?.mediaDTO || _.get(entity, "icon", null));
    setDescription(shareTemplateData?.message || '');
    setDescriptionsForTones(initialDescriptionTones);
    dispatch(invalidateLinkedInPost());
    dispatch(invalidateLinkedInPostTones(entity.id));
  }


  const onChange = (description: string) => {
    const parsedValue = parseHTMLtoString(description);
    setDescription(parsedValue);
  }

  const sharePost = (scheduledDate: string, scheduledTime: string, scheduleTimeZone: string) => {
    if (_.size(description) === 0) {
      setEmptyDescriptionError(true);
      setTimeout(() => {
        setEmptyDescriptionError(false);
      }, 3000)
      return;
    }
   const { content, attributes } = extractContentAndAttributes(description);

    dispatch(shareLinkedInPostRequest({
      requestBody: {
        sharedUrl: shareUrl.url,
        sharedUrlId: shareUrl.id,
        media: _.get(banner, "id", ""),
        message: content,
        attributes: attributes,
        rawMessage: description,
        scheduledDate: scheduledDate,
        scheduledTime: scheduledTime,
        scheduleTimeZone: scheduleTimeZone,
        postType: scheduledDate ? "Scheduled" : "Instant"
      },
      entityId: entity.id,
      entityType: ContentType.Asset
    }))
  }

  const handleDropdownItemClick = (selectedItem: IDescriptionTune) => {

    if (!selectedItem.description) {

      if(_.size(description) === 0){
        setDisableRephrase(true);
        setTimeout(() => {
          setDisableRephrase(false);
        }, 5000)
        return;
      }

      const trimmedTone = _.replace(selectedItem.title, " ", "");
      const tone = _.toUpper(trimmedTone) as IRephraseTones;

      dispatch(repharseLinkedInPostDescription({
        message: description,
        rephraseTone: tone,
        entityId: entity.id
      }))

    }
    if (_.size(selectedItem.description) > 0) {
      setDescription(selectedItem.description)
    }

    setDescriptionsForTones(descriptionsForTones.map((item) => {
      if (item.title === selectedItem.title) {
        return { ...item, active: true }
      }
      return { ...item, active: false }
    }))

  }

  const getWarningMessage = () => {
    if(disableRephrase){
      return "Please add a description to change it's tone";
    }
    if (emptyDescriptionError) {
      return "Description cannot be empty";
    }
    if (isWordLimitExceeded) {
      return "Word limit exceeded";
    }
    return "";
  }

const extractContentAndAttributes = (htmlContent: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  let content = htmlContent
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&nbsp;/gi, ' ');

  content = content.replace(/<[^>]+>/g, ' ');

  const contentWithLinks = content.replace(/{link}/g, shareUrl.url);

  if (!htmlContent || !htmlContent.includes('data-original-variable')) {
    return { content, attributes: [] };
  }
  const organizationMentions = doc.querySelectorAll('span[data-original-variable][data-content-type="linkedcompany"]');
  const attributes: any[] = [];
  let searchFromPosition = 0;

  organizationMentions.forEach((span) => {
    const organizationId = span.getAttribute('data-original-variable');
    const fullText = span.textContent?.trim() || '';
    const companyName = fullText.trim();

    if (organizationId && companyName) {
      const position = contentWithLinks.indexOf(companyName, searchFromPosition);

      if (position !== -1) {
        attributes.push({
          start: position,
          length: companyName.length,
          companyUrin: organizationId
        });
        searchFromPosition = position + companyName.length;
      }
    }
  });

  return { content, attributes };
  
};

  const handleSchedule = () => {
    if (selectedDate && selectedTime) {

      const scheduledDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        selectedTime.getHours(),
        selectedTime.getMinutes()
      );

    const scheduledDate = scheduledDateTime.toISOString().split('T')[0];
  
    const hours = scheduledDateTime.getHours().toString().padStart(2, '0');
    const minutes = scheduledDateTime.getMinutes().toString().padStart(2, '0');
    const scheduledTime = `${hours}:${minutes}`;

    const scheduleTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    sharePost(scheduledDate, scheduledTime, scheduleTimeZone);
    }
  };

  const handleClose = () => {
    dispatch(invalidateLinkedInPosts());
    setShowAllPosts(false);
  }

  return (
    <Slider.Dialog width={showEditBanner || showAllPosts ? sliderWidth : "615px"} show={show} onClose={onCancel}>

      {!showEditBanner && !showAllPosts && <div className={styles.linkedInPost}>

        {isLinkedInIntegrationActive && <>
          <div className={styles.postContainer}>
            <p className={styles.header}>
            <div className={styles.title}>
              <span>share on </span>
              <img src={LinkedIn} />
            </div>
            <i className={cx('paperflite-close', styles.closeIcon)} 
              onClick={() => {
                setDescription(shareTemplateData?.message || '')
                setBanner(shareTemplateData?.mediaDTO || _.get(entity, "icon", null))
                onCancel()
                dispatch(invalidateLinkedInPosts());
              }} />
            </p>
            <div className={styles.user}>
              <img src={_.get(user, "icon.full", DefaultUserAvatar)} />
              <span>{_.get(user, "firstName", "")} {_.get(user, "lastName", "")}</span>

              <Dropdown>

                <Dropdown.Toggle as="div" className={styles.dropDown}>
                  <img src={Seek} />  {_.get(activeTone, "title", "Change Tone")} <i className='paperflite-dropdown' />

                </Dropdown.Toggle>

                <Dropdown.Menu className={styles.dropdownMenu}>
                  {descriptionsForTones.map((tone, index) => (
                    <Dropdown.Item
                      key={index}
                      className={cx(styles.dropdownItem, { [styles.active]: tone.active })}
                      onClick={() => handleDropdownItemClick(tone)}
                    >
                      {tone.title}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div className={styles.content}>

            {isRephrasing && <div className={styles.loadingContainer}>
              {_.times(3, (index) => (
                <div key={index} className={styles.loader} />
              ))}
            </div>}

              <div className={styles.descriptionContainer}>
                {!isRephrasing && <>
                <DescriptionEditor 
                  content={description}
                  onChange={onChange}
                  widgetManager={widgetManager.current}
                  className={styles.description}
                  tones={descriptionsForTones}
                  setLimitReached = {setIsWordLimitExceeded}
                />
                <span className={styles.wordLimit}>*character limit: 3000</span></>}
                <a href="#" className={styles.link}>https://lnkd.in/uniqueID</a>
                <Tooltip content={"The asset link will be attached to your LinkedIn post when it's shared"} placement='top'>
                  <i className={cx('paperflite-info', styles.tooltip)} />
                </Tooltip>
              </div>

         
              <div onClick={() => setShowEditBanner(true)} className={styles.banner}>
                {!banner?.full && <div className={styles.uploadBanner}>
                  <i className={cx("paperflite-upload")} />
                  <span>upload artwork</span>
                </div>}
                <i className={cx('paperflite-edit', styles.editIcon)} />
                {banner?.full && <img src={banner?.full} />}
              </div>
            </div>
          </div>

          <div className={styles.footer}>

            {(isWordLimitExceeded || emptyDescriptionError || disableRephrase) && (
              <div className={styles.warningMessage}>
                <WarningMessage
                  type="ERROR"
                  header={getWarningMessage()}
                  message=""
                />
              </div>
            )}

            {(postCount.posted > 0 || postCount.scheduled > 0) && (
              <p className={styles.linkedInNote}>
                Note* – LinkedIn shares:{' '}
                {postCount.posted > 0 && (
                  <span className={styles.highlight}>{postCount.posted} published</span>
                )}
                {postCount.posted > 0 && postCount.scheduled > 0 && ' · '}
                {postCount.scheduled > 0 && (
                  <span className={styles.highlight}>{postCount.scheduled} scheduled</span>
                )}
              </p>
            )}

            <div className={styles.buttonContainer}>

              <div className={styles.actionButtons}>
                <Button
                  variant="primary"
                  className={styles.saveButton}
                  icon="paperflite-right-arrow"
                  text={scheduleLater ? "Schedule" : (isPosting ? "Sharing..." : postCount.posted > 0 ? "Share again" : "Share")}
                  disabled={_.size(_.get(shareUrl, "url", "")) === 0 || isPosting || isWordLimitExceeded || emptyDescriptionError}
                  onClick={scheduleLater ? handleSchedule : () => sharePost(scheduledDate, scheduledTime, scheduleTimeZone)}
                />

                {scheduleLater ? (
                  <SchedulePicker 
                  onClose={() => setScheduleLater(false)}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  selectedTime={selectedTime}
                  setSelectedTime={setSelectedTime}
                  />
                ) : (
                <Button
                  variant="secondary"
                  className={styles.cancelButton}
                  icon="paperflite-clock"
                  onClick={() => {
                    setScheduleLater(true);
                  }}
                  text="Schedule Later"
                />)}
              </div>

              <div className={styles.allPosts} onClick={() => setShowAllPosts(true)}>
                <i className={cx("paperflite-right-arrow", styles.allPostsIcon)} />
                <span>All scheduled posts</span>
              </div>
            </div>
          </div>
        </>
        }
        {(!isLinkedInIntegrationActive || isLinkedInIntegrationExpired) && <>
          <i onClick={onCancel} className={cx(styles.closeIcon, "paperflite-close")} />

          <div className={styles.emptyMessageContainer}>
            <div className={styles.emptyMessage}>

              <img src={EmptyMessage} />
              <p> {isLinkedInIntegrationExpired ? "Your authentication to LinkedIn has expired and needs to be renewed." : "Seems like you're not integrated with your LinkedIn account."}</p>
              <Button
                variant="primary"
                className={styles.redirectButton}
                icon="paperflite-right-arrow"
                text={"Go to integrations"}
                disabled={false}
                onClick={() => navigate("/settings/integrations/linkedIn")}
              />
            </div>

          </div>
        </>}


      </div>}

      <div className={cx(styles.editBannerContainer, {
        [styles.showEditBanner]: showEditBanner
      })}>

        <EditBannerComponent
          show={showEditBanner}
          setSliderWidth={setSliderWidth}
          onClose={() => setShowEditBanner(false)}
          setBanner={(media) => setBanner(media)}
          banner={banner}
        />
      </div>

      <div className={cx(styles.allPostsContainer, {
        [styles.showAllPosts]: showAllPosts
      })}>
        <ScheduledPosts onClose={handleClose}/>
      </div>

    </Slider.Dialog>
  )
}

const initialDescriptionTones = [
  {
    title: DescriptionToneEnum.FORMAL,
    description: ""
  },
  {
    title: DescriptionToneEnum.INFORMAL,
    description: ""
  },
  {
    title: DescriptionToneEnum.LIGHTHEARTED,
    description: ""
  }
]

export default LinkedInPost;